import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { MiPlaceholderArea } from "@miplatform/components";
import { useRouter } from "@miplatform/hooks";
import { SidebarLayout } from "@mibuildweb/components";
import { Box } from "@mui/system";
import { HomeFilterSidebar } from "./components/HomeFilterSidebar";
import { HomeSearchAutocomplete } from "./components/HomeSearchAutocomplete";
import { HomesPageHeader } from "./components/HomesPageHeader";
import { useHomeFilters } from "./hooks/useHomeFilters";
import { useHomeSearchParams } from "./hooks/useHomeSearchParams";
import { useHomesViewModel } from "./hooks/useHomesViewModel";
import { HomeFilter } from "./types";

export function Homes() {
  const { query, push } = useRouter();
  const { classes, theme } = useStyles();
  const { isLoading, search, setSearch } = useHomesViewModel();
  const { buildListUrlWithSearchParams } = useHomeSearchParams();

  const {
    filters,
    resetFilters,
    homePhaseOptions,
    builderOptions,
    communityOptions,
    orderTemplateOptions,
    getCommunities,
    getBuilders,
    getOrderTemplateOptions,
    isLoading: isFiltersLoading,
  } = useHomeFilters();

  const handleClearSearchInput = () => {
    setSearch("");
  };

  const handleNavigateToListWithFilters = (filters: HomeFilter) => {
    const url = buildListUrlWithSearchParams(filters);
    push(url);
  };

  const handleSearchEnterPress = () => {
    if (search.trim().length > 0) {
      push(`/homes/list?search=${encodeURIComponent(search.trim())}`, {});
    }
  };

  useEffect(() => {
    Promise.allSettled([
      getBuilders(),
      getCommunities(),
      getOrderTemplateOptions(),
    ]);

    if (query.search) {
      setSearch(query.search as string);
    }
  }, []);

  return (
    <>
      <HomesPageHeader isLoading={isLoading} />
      <SidebarLayout
        sidebarContent={
          <HomeFilterSidebar
            customerOptions={builderOptions}
            communityOptions={communityOptions}
            filters={filters}
            resetFilters={resetFilters}
            onApplyFilters={handleNavigateToListWithFilters}
            orderTemplateOptions={orderTemplateOptions}
            phaseOptions={homePhaseOptions}
            loading={isFiltersLoading || isLoading}
          />
        }
      >
        <Box className={classes.searchContainerDefault}>
          <MiPlaceholderArea
            text=""
            showLogo
            helperText="Search for a home or an order"
            containerStyle={{
              backgroundColor: theme.palette.common.white,
              height: "auto",
            }}
            logoStyle={{ height: 64 }}
          />
          <HomeSearchAutocomplete
            search={search}
            onSearchChange={setSearch}
            onClearSearch={handleClearSearchInput}
            onEnterPress={handleSearchEnterPress}
          />
        </Box>
      </SidebarLayout>
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  searchContainerDefault: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "center",
    marginTop: theme.spacing(16),
  },
}));
